/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useGlobalHeader} from '../../contexts/global-elements-context'
import {mapScreenIdToHandler} from '../../utils/optimizely-header-utils'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Text,
    Link,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useHeaderContext} from '../../contexts/header-context'
import {useHeaderHeight} from '../../hooks/use-header-height'
import {useStickyState} from '../../contexts/sticky-context'
import withRegistration from '../with-registration'
import {BrandLogo} from '@salesforce/retail-react-app/app/components/icons'
import {Image} from '@salesforce/retail-react-app/app/components/shared/ui'

import {MiniCartIcon, ProfileIcon, WishlistIcon, HeaderSearchIcon, HeaderMenuIcon} from '../icons'

import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useSearchModal} from '../../contexts/search-modal-context'
import { background } from '@chakra-ui/react'

const IconButtonWithRegistration = withRegistration(IconButton)

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
    children,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    onWishlistClick = noop,
    onStoreLocatorClick = noop,
    ...props
}) => {
    const intl = useIntl()
    const {headerRef, shouldShowSticky} = useHeaderContext()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {openSearchModal} = useSearchModal()

    // Fetch global header content from global elements context
    const {data: headerData} = useGlobalHeader()

    // CMS-driven content with fallbacks from new global structure
    const logoData = headerData?.logo
    const searchPlaceholder = headerData?.search?.placeholder
    const iconNavigation = headerData?.iconNavigation || []

    // Get styles for header components
    const styles = useMultiStyleConfig('Header')

    // Create logo component with CMS data fallback
    const LogoComponent = logoData?.url ? (
        <Image
            src={logoData.url}
            {...styles.logo}
            width="110px"
            height="20px"
            alt=""
        />
    ) : (
        <BrandLogo {...styles.logo} width="110px" height="20px" />
    )

    // Create mapping for action handlers
    const actionHandlers = {
        onSearchClick: () => openSearchModal(searchPlaceholder),
        onAccountClick: onMyAccountClick,
        onCartClick: onMyCartClick,
        onWishlistClick: onWishlistClick
    }


    // Find CMS icons for each action using the new data structure
    const searchIconData = iconNavigation.find((item) => item.link?.screenId === 'search')
    const accountIconData = iconNavigation.find((item) => item.link?.screenId === 'account')
    const cartIconData = iconNavigation.find((item) => item.link?.screenId === 'bag')
    const wishlistIconData = iconNavigation.find((item) => item.link?.screenId === 'wishlist')

    const {onOpen: onOpenMiniCartOpen} = useMiniCartContext()

    const [scrollDir, setScrollDir] = useState('up')
    const lastScrollY = useRef(0)

    // Calculates header height
    useHeaderHeight()

    const {pdpStickyButtonActive} = useStickyState()
    const shouldShowHeader = scrollDir === 'up' || (!shouldShowSticky && !pdpStickyButtonActive)

    const headerContentStyle = shouldShowHeader
        ? {
              transition: 'transform 0.3s ease',
              position: 'static'
          }
        : {
              transform: 'translateY(-100%)',
              transition: 'none',
              position: 'absolute'
          }

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY

            if (Math.abs(currentY - lastScrollY.current) < 5) return

            if (currentY > lastScrollY.current) {
                setScrollDir('down')
            } else {
                setScrollDir('up')
            }

            lastScrollY.current = currentY
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <Box id="app-header" {...styles.container} {...props} ref={headerRef}>
            <Box
                maxWidth={styles.content?.maxWidth}
                marginLeft={styles.content?.marginLeft}
                marginRight={styles.content?.marginRight}
                padding={{base: '10px 16px', lg: '0 24px'}}
                height="48px"
                style={{
                    ...headerContentStyle
                }}
            >
                <Flex
                    wrap="nowrap"
                    alignItems="center"
                    justify="space-between"
                    direction="row"
                    width="100%"
                >
                    <Flex alignItems="center" gap={2}>
                        <HideOnDesktop>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                title={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu.open_dialog',
                                    defaultMessage: 'Opens a dialog'
                                })}
                                icon={<HeaderMenuIcon />}
                                variant="unstyled"
                                {...styles.icons}
                                onClick={onMenuClick}
                                width="24px"
                                height="24px"
                                minWidth="unset"
                                data-testid="header-menu-button"
                            />
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.search',
                                    defaultMessage: 'Search'
                                })}
                                icon={
                                    searchIconData?.icon?.url ? (
                                        <Image
                                            src={searchIconData.icon.url}
                                            alt=""
                                            width="24px"
                                            height="24px"
                                        />
                                    ) : (
                                        <HeaderSearchIcon />
                                    )
                                }
                                variant="unstyled"
                                {...styles.icons}
                                width="24px"
                                height="24px"
                                minWidth="unset"
                                data-testid="header-button-search-mobile"
                                marginLeft={2}
                                onClick={() => openSearchModal(searchPlaceholder)}
                            />
                        </HideOnDesktop>

                        <HideOnMobile>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo'
                                })}
                                icon={LogoComponent}
                                {...styles.icons}
                                variant="unstyled"
                                onClick={onLogoClick}
                                display="block"
                                height="auto"
                                data-testid="header-logo"
                            />
                        </HideOnMobile>
                    </Flex>

                    <Flex flex="1" justify="center">
                        <HideOnMobile>
                            <Box {...styles.bodyContainer}>{children}</Box>
                        </HideOnMobile>

                        <HideOnDesktop>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo'
                                })}
                                icon={LogoComponent}
                                {...styles.icons}
                                variant="unstyled"
                                onClick={onLogoClick}
                                display="block"
                                height="auto"
                                data-testid="header-logo"
                            />
                        </HideOnDesktop>
                    </Flex>

                    <Flex alignItems="center" gap={2}>
                        <HideOnMobile>
                            <IconButton
                                data-testid="header-search-button"
                                as={Link}
                                href={'#'}
                                aria-label="Open search drawer"
                                icon={
                                    searchIconData?.icon?.url ? (
                                        <Image
                                            alt=""
                                            src={searchIconData.icon.url}
                                            width="24px"
                                            height="24px"
                                        />
                                    ) : (
                                        <HeaderSearchIcon />
                                    )
                                }
                                display="block"
                                variant="ghost"
                                minWidth="unset"
                                width="24px"
                                height="24px"
                                onClick={(e) => {
                                    if (e) e.preventDefault()
                                    openSearchModal(searchPlaceholder)
                                }}
                                _hover={{
                                    background: 'unset'
                                }}
                            />
                        </HideOnMobile>

                        <HideOnMobile>
                            <IconButton
                                data-testid="header-wishlist-button"
                                as={Link}
                                href={'/account/wishlist'}
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Go to Wishlist page',
                                    id: 'header.button.assistive_msg.wishlist'
                                })}
                                icon={
                                    wishlistIconData?.icon?.url ? (
                                        <Image
                                            src={wishlistIconData.icon.url}
                                            alt=""
                                            width="24px"
                                            height="24px"
                                        />
                                    ) : (
                                        <WishlistIcon aria-hidden={null} aria-label={null} />
                                    )
                                }
                                variant="unstyled"
                                onClick={(e) => {
                                    if (e) e.preventDefault()
                                    onWishlistClick()
                                }}
                                width="24px"
                                height="24px"
                                minWidth="unset"
                                display="block"
                            />
                        </HideOnMobile>

                        <IconButtonWithRegistration
                            data-testid="header-account-button"
                            as={Link}
                            href={'/account'}
                            icon={
                                accountIconData?.icon?.url ? (
                                    <Image
                                        alt=""
                                        src={accountIconData.icon.url}
                                        width="24px"
                                        height="24px"
                                    />
                                ) : (
                                    <ProfileIcon />
                                )
                            }
                            aria-label={intl.formatMessage({
                                id: 'header.button.assistive_msg.my_account',
                                defaultMessage: 'Open my account modal'
                            })}
                            variant="unstyled"
                            {...styles.icons}
                            {...styles.accountIcon}
                            onClick={(e) => {
                                if(e) e.preventDefault()
                                onMyAccountClick()
                            }}
                            height="24px"
                            display="block"
                        />

                        <IconButton
                            data-testid="header-cart-button"
                            as={Link}
                            href={'/cart'}
                            aria-label={intl.formatMessage(
                                {
                                    id: 'header.button.assistive_msg.my_cart_with_num_items',
                                    defaultMessage: 'My cart, number of items: {numItems}'
                                },
                                {numItems: totalItems}
                            )}
                            icon={
                                <>
                                    {cartIconData?.icon?.url ? (
                                        <Image
                                            alt=""
                                            src={cartIconData.icon.url}
                                            width="24px"
                                            height="24px"
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    ) : (
                                        <MiniCartIcon width="24px"  aria-hidden="true" focusable="false"/>
                                    )}
                                    {basket && totalItems > 0 && (
                                        <Text ml={1} fontWeight="normal" fontSize="xs">
                                            ({totalItems})
                                        </Text>
                                    )}
                                </>
                            }
                            variant="unstyled"
                            {...styles.icons}
                            onClick={(e) => {
                                if (e) e.preventDefault()

                                if (isDesktop) {
                                    onOpenMiniCartOpen()
                                } else {
                                    onMyCartClick()
                                }
                            }}
                            display="flex"
                            height="24px"
                            minWidth="unset"
                            _hover={{
                                textDecoration: 'unset'
                            }}
                        />
                    </Flex>
                </Flex>
            </Box>
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
